import type { Task as FhirTask } from "fhir/r5";

import {
  CRM_TASK_INPUT_REQUESTED_COUNT_CODE,
  CRM_TASK_OUTPUT_SKIPPED_COUNT_CODE,
  CRM_TASK_OUTPUT_SUCCESS_COUNT_CODE,
  CRM_TASK_OUTPUT_TOTAL_PROCESSED_CODE,
} from "../../statistics/constants/crmMessageFranchiseFhirConstants";
import { isCrmBatchNotificationTask } from "../../statistics/helpers/crmMessageFranchiseStatsHelpers";
import { patientIdFromReference } from "../aggregations";
import { FLUXZAP_TASK_BATCH_GROUP_IDENTIFIER_SYSTEM } from "../constants/clinicalCrmCommunicationsFhirConstants";
import type {
  FluxZapBatchTaskStatus,
  FluxZapBatchTaskView,
  FluxZapCommunicationRecord,
} from "../types";
import { humanizeTemplateCode } from "./mapFhirBasicToFluxZapCommunicationRecord";

function readTaskInputInteger(task: FhirTask, code: string): number {
  const value = task.input?.find((i) => i.type?.coding?.[0]?.code === code)
    ?.valueInteger;
  return typeof value === "number" ? value : 0;
}

function readTaskOutputInteger(task: FhirTask, code: string): number {
  const value = task.output?.find((o) => o.type?.coding?.[0]?.code === code)
    ?.valueInteger;
  return typeof value === "number" ? value : 0;
}

function mapTaskStatus(status: FhirTask["status"]): FluxZapBatchTaskStatus {
  if (status === "completed") return "completed";
  if (status === "in-progress") return "in-progress";
  if (
    status === "failed" ||
    status === "cancelled" ||
    status === "rejected"
  ) {
    return "failed";
  }
  if (status === "draft") return "draft";
  return "unknown";
}

/** Extrai código do template da description da Task (ex.: "... template X para N pacientes."). */
export function extractTemplateCodeFromTaskDescription(
  description: string | undefined,
): string | undefined {
  if (!description?.trim()) return undefined;
  const match = description.match(/template\s+(\S+)\s+para/i);
  return match?.[1]?.trim() || undefined;
}

function pickBatchId(task: FhirTask): string {
  const group = task.groupIdentifier;
  if (
    group?.system === FLUXZAP_TASK_BATCH_GROUP_IDENTIFIER_SYSTEM &&
    group.value?.trim()
  ) {
    return group.value.trim();
  }
  return group?.value?.trim() ?? task.id ?? "";
}

function communicationsForBatch(
  communications: FluxZapCommunicationRecord[],
  batchId: string,
): FluxZapCommunicationRecord[] {
  return communications.filter((c) => c.batchId === batchId);
}

/**
 * Destinatários do lote: Basics com batchId; fallback por template + janela temporal.
 * Limitação: sem batchId no Basic, a lista pode ser aproximada.
 */
function resolveRecipientPatientIds(
  task: FhirTask,
  batchId: string,
  templateCode: string,
  communications: FluxZapCommunicationRecord[],
): string[] {
  const byBatch = communicationsForBatch(communications, batchId);
  if (byBatch.length > 0) {
    const ids = new Set<string>();
    for (const msg of byBatch) {
      const pid = patientIdFromReference(msg.subjectPatientReference);
      if (pid) ids.add(pid);
    }
    return [...ids];
  }

  if (!templateCode) return [];

  const authoredMs = task.authoredOn
    ? new Date(task.authoredOn).getTime()
    : NaN;
  const windowMs = 5 * 60 * 1000;
  const sameTemplate = communications.filter((c) => c.templateCode === templateCode);

  const inWindow = Number.isFinite(authoredMs)
    ? sameTemplate.filter((c) => {
        const createdMs = new Date(c.created).getTime();
        return (
          Number.isFinite(createdMs) &&
          Math.abs(createdMs - authoredMs) <= windowMs
        );
      })
    : sameTemplate;

  const ids = new Set<string>();
  for (const msg of inWindow) {
    const pid = patientIdFromReference(msg.subjectPatientReference);
    if (pid) ids.add(pid);
  }
  return [...ids];
}

function buildSingleBatchTaskView(
  task: FhirTask,
  communications: FluxZapCommunicationRecord[],
): FluxZapBatchTaskView | null {
  if (!isCrmBatchNotificationTask(task)) return null;

  const batchId = pickBatchId(task);
  const batchComms = communicationsForBatch(communications, batchId);
  const templateFromBasic = batchComms[0]?.templateCode;
  const templateFromDescription = extractTemplateCodeFromTaskDescription(
    task.description,
  );
  const templateCode =
    templateFromBasic ?? templateFromDescription ?? "desconhecido";
  const templateLabel =
    batchComms[0]?.templateLabel ?? humanizeTemplateCode(templateCode);

  const focusFromTask = task.focus?.reference?.trim();
  const focusFromBasic = batchComms[0]?.id
    ? `Basic/${batchComms[0].id}`
    : "";
  const focusBasicReference = focusFromTask || focusFromBasic || "—";

  const recipientPatientIds = resolveRecipientPatientIds(
    task,
    batchId,
    templateCode,
    communications,
  );

  return {
    taskId: task.id ?? batchId,
    batchId,
    status: mapTaskStatus(task.status),
    intent: task.intent,
    priority: task.priority,
    description: task.description ?? "",
    authoredOn: task.authoredOn ?? task.meta?.lastUpdated ?? "",
    lastModified: task.lastModified ?? task.meta?.lastUpdated,
    requesterReference: task.requester?.reference,
    ownerReference: task.owner?.reference,
    focusBasicReference,
    templateCode,
    templateLabel,
    requestedCount: readTaskInputInteger(task, CRM_TASK_INPUT_REQUESTED_COUNT_CODE),
    successCount: readTaskOutputInteger(
      task,
      CRM_TASK_OUTPUT_SUCCESS_COUNT_CODE,
    ),
    skippedCount: readTaskOutputInteger(
      task,
      CRM_TASK_OUTPUT_SKIPPED_COUNT_CODE,
    ),
    totalProcessed: readTaskOutputInteger(
      task,
      CRM_TASK_OUTPUT_TOTAL_PROCESSED_CODE,
    ),
    recipientPatientIds,
  };
}

/** Converte Tasks BATCH_NOTIFICATION + communications em visões para a UI. */
export function mapFhirTasksToFluxZapBatchTaskViews(
  tasks: FhirTask[],
  communications: FluxZapCommunicationRecord[],
): FluxZapBatchTaskView[] {
  return tasks
    .map((task) => buildSingleBatchTaskView(task, communications))
    .filter((view): view is FluxZapBatchTaskView => view != null);
}
